import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronLeft, Search, Truck } from 'lucide-react';
import { apiBase, authHeaders, resolveOrgId } from '../../utils/authHeaders';
import CameraCapture from '../../capture/CameraCapture';
import { STATUS, flushQueue, listAll, retryFailed } from '../../capture/photoQueue';

const API_URL = apiBase();

/**
 * /mobile/receiving — photograph the delivery note, then receive against the PO.
 *
 * Steps 1 and 2 work with no connection (the PO list is cached once seen, and
 * photos go to the durable queue). Step 3 posts to the existing mobile receive
 * endpoint and is therefore marked "needs connection".
 *
 * There are deliberately no delivery-note suggestions and no temperature entry:
 * the backend does not support either yet.
 */
export default function MobileReceiving({ token, userId, onDone }) {
  const [step, setStep] = useState(1);
  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [quantities, setQuantities] = useState({});
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);

  const orgId = useMemo(() => resolveOrgId(), []);
  const locationId = typeof localStorage !== 'undefined' ? localStorage.getItem('current_location_id') : null;
  const online = typeof navigator === 'undefined' || navigator.onLine !== false;

  const refreshQueue = useCallback(async () => { setRows(await listAll()); }, []);

  const loadOrders = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/mobile/inventory/purchase-orders`, {
        headers: authHeaders({ token, orgId, locationId, json: true }),
      });
      if (!res.ok) {
        // Offline or refused: fall back to whatever we last saw, if anything.
        const cached = typeof localStorage !== 'undefined'
          ? localStorage.getItem('mobile_receiving_orders')
          : null;
        if (cached) { setOrders(JSON.parse(cached)); setNotice('Showing the last list seen on this device.'); return; }
        setError(res.status === 403 ? 'You do not have permission to receive deliveries.' : 'Could not load purchase orders.');
        setOrders([]);
        return;
      }
      const data = await res.json();
      const list = Array.isArray(data) ? data : data.items || [];
      setOrders(list);
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('mobile_receiving_orders', JSON.stringify(list));
      }
    } catch {
      const cached = typeof localStorage !== 'undefined'
        ? localStorage.getItem('mobile_receiving_orders')
        : null;
      if (cached) { setOrders(JSON.parse(cached)); setNotice('Showing the last list seen on this device.'); }
      else setError('Could not reach the server.');
    } finally {
      setLoading(false);
    }
  }, [token, orgId, locationId]);

  useEffect(() => { loadOrders(); refreshQueue(); }, [loadOrders, refreshQueue]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return orders;
    return orders.filter((po) => String(po.supplier_name || '').toLowerCase().includes(q)
      || String(po.po_number || po.id || '').toLowerCase().includes(q));
  }, [orders, search]);

  const lines = selected?.lines || [];

  const doFlush = useCallback(async () => {
    setBusy(true);
    setNotice(null);
    try {
      const results = await flushQueue({ apiUrl: API_URL, token, orgId, userId, locationId });
      const sent = results.filter((r) => r.status === STATUS.SENT).length;
      const failed = results.filter((r) => r.status === STATUS.FAILED).length;
      setNotice(sent || failed ? `${sent} uploaded${failed ? `, ${failed} failed` : ''}.` : 'Nothing to upload.');
      await refreshQueue();
    } finally {
      setBusy(false);
    }
  }, [token, orgId, userId, locationId, refreshQueue]);

  const submitReceive = useCallback(async () => {
    if (!selected) return;
    const items = {};
    lines.forEach((ln) => {
      const key = ln.ingredient_id || ln.item_id;
      const raw = quantities[key];
      if (raw !== undefined && raw !== '') items[key] = Number(raw);
    });
    if (Object.keys(items).length === 0) { setError('Enter at least one received quantity.'); return; }
    setBusy(true);
    setError(null);
    try {
      // Exactly the body the endpoint expects.
      const res = await fetch(`${API_URL}/api/mobile/inventory/purchase-orders/${selected.id}/receive`, {
        method: 'POST',
        headers: authHeaders({ token, orgId, locationId, json: true }),
        body: JSON.stringify({ items }),
      });
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        setError(detail.detail || `Could not record the delivery (${res.status}).`);
        return;
      }
      setNotice('Delivery recorded.');
      setQuantities({});
      setSelected(null);
      setStep(1);
      await loadOrders();
      if (onDone) onDone();
    } catch {
      setError('Could not reach the server. Receiving needs a connection.');
    } finally {
      setBusy(false);
    }
  }, [selected, lines, quantities, token, orgId, locationId, loadOrders, onDone]);

  const statusLabel = (row) => {
    if (row.status === STATUS.SENT) return 'sent';
    if (row.status === STATUS.FAILED) return `failed${row.last_error ? ` — ${row.last_error}` : ''}`;
    if (row.status === STATUS.SENDING) return 'sending';
    return row.attempts ? `queued (retry ${row.attempts})` : 'queued';
  };

  return (
    <div className="min-h-screen bg-surface-muted pb-24" data-testid="mobile-receiving">
      <header className="sticky top-0 z-10 bg-surface border-b border-surface-border px-4 py-3 flex items-center gap-3">
        {step > 1 && (
          <button
            type="button"
            aria-label="Back"
            data-testid="receiving-back"
            className="min-w-[44px] min-h-[44px] flex items-center justify-center"
            onClick={() => setStep(step - 1)}
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
        )}
        <h1 className="font-display text-base text-ink">
          {step === 1 ? 'Choose a delivery' : step === 2 ? 'Photograph the note' : 'Received quantities'}
        </h1>
      </header>

      {notice && <p className="px-4 py-2 text-sm text-ink-muted" data-testid="receiving-notice">{notice}</p>}
      {error && (
        <p className="px-4 py-2 text-sm text-danger" role="alert" data-testid="receiving-error">{error}</p>
      )}

      {step === 1 && (
        <section className="p-4 space-y-3" data-testid="receiving-step-1">
          <label className="block">
            <span className="sr-only">Search by supplier</span>
            <div className="flex items-center gap-2 border border-surface-border rounded px-3 bg-surface">
              <Search className="w-4 h-4 text-ink-muted" />
              <input
                className="flex-1 py-2 min-h-[44px] bg-transparent outline-none"
                placeholder="Search by supplier"
                data-testid="receiving-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </label>

          {loading && <p className="text-sm text-ink-muted" data-testid="receiving-loading">Loading…</p>}
          {!loading && filtered.length === 0 && (
            <p className="text-sm text-ink-muted" data-testid="receiving-empty">No open purchase orders.</p>
          )}
          <ul className="space-y-2" data-testid="receiving-orders">
            {filtered.map((po) => (
              <li key={po.id}>
                <button
                  type="button"
                  className="w-full text-left bg-surface border border-surface-border rounded-lg p-3 min-h-[44px]"
                  data-testid={`receiving-order-${po.id}`}
                  onClick={() => { setSelected(po); setStep(2); }}
                >
                  <div className="flex items-center gap-2">
                    <Truck className="w-4 h-4 text-ink-muted" />
                    <span className="font-medium text-ink">{po.supplier_name || 'Supplier'}</span>
                  </div>
                  <div className="text-xs text-ink-muted mt-1">
                    {po.po_number || po.id} · {po.expected_date || 'no date'} · {(po.lines || []).length} lines
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {step === 2 && selected && (
        <section className="p-4 space-y-4" data-testid="receiving-step-2">
          <p className="text-sm text-ink-muted">
            {selected.supplier_name} · {selected.po_number || selected.id}
          </p>

          <CameraCapture
            captureType="delivery_note"
            organizationId={orgId}
            locationId={locationId}
            userId={userId}
            apiUrl={API_URL}
            onQueued={refreshQueue}
          />

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="border border-surface-border px-4 py-2 min-h-[44px] rounded disabled:opacity-40"
              data-testid="receiving-flush"
              onClick={doFlush}
              disabled={busy || !online}
            >
              Upload photos
            </button>
            {!online && (
              <span className="text-sm text-ink-muted" data-testid="receiving-offline">
                Offline — photos stay on this device.
              </span>
            )}
          </div>

          <section className="bg-surface border border-surface-border rounded-lg p-3">
            <h2 className="text-sm font-medium text-ink mb-2">Photos on this device</h2>
            {rows.length === 0 ? (
              <p className="text-sm text-ink-muted" data-testid="receiving-queue-empty">Nothing waiting.</p>
            ) : (
              <ul className="space-y-2" data-testid="receiving-queue">
                {rows.map((row) => (
                  <li key={row.id} className="flex items-center gap-3 text-sm" data-testid={`receiving-photo-${row.id}`}>
                    <span
                      className={row.status === STATUS.FAILED ? 'text-danger' : 'text-ink'}
                      data-testid={`receiving-photo-status-${row.id}`}
                    >
                      {statusLabel(row)}
                    </span>
                    {row.status === STATUS.FAILED && (
                      <button
                        type="button"
                        className="underline min-h-[44px]"
                        data-testid={`receiving-photo-retry-${row.id}`}
                        onClick={async () => { await retryFailed(row.id); await refreshQueue(); }}
                      >
                        Retry
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>

          <button
            type="button"
            className="w-full bg-primary-500 text-ink-inverse px-4 py-3 min-h-[44px] rounded"
            data-testid="receiving-to-quantities"
            onClick={() => setStep(3)}
          >
            Enter quantities
          </button>
        </section>
      )}

      {step === 3 && selected && (
        <section className="p-4 space-y-4" data-testid="receiving-step-3">
          <p className="text-sm text-ink-muted" data-testid="receiving-needs-connection">
            Recording the delivery needs a connection.
          </p>
          <ul className="space-y-3">
            {lines.map((ln, idx) => {
              const key = ln.ingredient_id || ln.item_id;
              return (
                <li key={`${key}-${idx}`} className="bg-surface border border-surface-border rounded-lg p-3">
                  <div className="font-medium text-ink">{ln.description || ln.item_name || key}</div>
                  <div className="text-xs text-ink-muted mt-1">
                    ordered {ln.qty ?? ln.ordered_qty ?? '—'} {ln.unit || ''}
                  </div>
                  <label className="block mt-2">
                    <span className="text-xs text-ink-muted">Received</span>
                    <input
                      type="number"
                      inputMode="decimal"
                      min="0"
                      step="any"
                      className="w-full border border-surface-border rounded px-3 py-2 min-h-[44px] bg-surface mt-1"
                      data-testid={`receiving-qty-${key}`}
                      value={quantities[key] ?? ''}
                      onChange={(e) => setQuantities((prev) => ({ ...prev, [key]: e.target.value }))}
                    />
                  </label>
                </li>
              );
            })}
          </ul>
          <button
            type="button"
            className="w-full bg-primary-500 text-ink-inverse px-4 py-3 min-h-[44px] rounded disabled:opacity-40"
            data-testid="receiving-confirm"
            onClick={submitReceive}
            disabled={busy}
          >
            Confirm delivery
          </button>
        </section>
      )}
    </div>
  );
}
